/**
 * Drift Side timeline builder.
 *
 * The /drift endpoint returns PRs and orphan commits as two separate sorted
 * arrays on each Side. For the drift UI (CORE-799), we want to render them
 * as ONE chronological timeline so the operator sees a single "what
 * changed" narrative — not two disconnected lists.
 *
 * A PR appears once at its merge time and represents its entire commit group
 * as a single card. Orphan commits (no merged PR) appear individually.
 *
 * Sort key: timestamp DESC (latest first). On identical timestamps, a PR
 * sorts BEFORE its associated commits (a merge happens after the push).
 */

import type { DriftPr, DriftCommit, DriftSide } from '@/types/api';

export type TimelineItem =
  | { readonly kind: 'pr'; readonly ts: string; readonly pr: DriftPr }
  | { readonly kind: 'commit'; readonly ts: string; readonly commit: DriftCommit };

export function buildTimeline(side: DriftSide): TimelineItem[] {
  const items: TimelineItem[] = [
    ...side.prs.map((pr): TimelineItem => ({ kind: 'pr', ts: pr.merged_at, pr })),
    ...side.orphan_commits.map(
      (commit): TimelineItem => ({ kind: 'commit', ts: commit.authored_at, commit }),
    ),
  ];

  // Sort: ts DESC. Tiebreak: PR before commit (merge follows push).
  items.sort((a, b) => {
    if (a.ts !== b.ts) return a.ts < b.ts ? 1 : -1;
    if (a.kind === b.kind) return 0;
    return a.kind === 'pr' ? -1 : 1;
  });

  return items;
}
